import React, { useState, useEffect, useRef, useCallback } from "react";
import { Bell, Check } from "lucide-react";
import { get, put } from "../api/client";
import { Notification } from "../types";

function timeAgo(dateStr: string): string {
  const now = new Date();
  const then = new Date(dateStr);
  const diff = Math.floor((now.getTime() - then.getTime()) / 1000);

  if (diff < 60) return "Zojuist";
  if (diff < 3600) return `${Math.floor(diff / 60)} min geleden`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} uur geleden`;
  return `${Math.floor(diff / 86400)} dag${Math.floor(diff / 86400) === 1 ? "" : "en"} geleden`;
}

const typeIcon: Record<string, string> = {
  info: "ℹ",
  warning: "⚠",
  success: "✓",
};

export default function NotificationBell() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  const fetchNotifications = useCallback(() => {
    get<Notification[]>("/notifications")
      .then(setNotifications)
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchNotifications();
    // Poll every 60 seconds
    const interval = setInterval(fetchNotifications, 60_000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  async function markAllRead() {
    try {
      await put("/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: 1 })));
    } catch {
      // ignore
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors duration-150 text-slate-500 hover:text-slate-700"
        aria-label="Meldingen"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 w-4 h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center leading-none">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[calc(100vw-2rem)] md:w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-semibold text-slate-800 text-sm">Meldingen</h3>
            {unreadCount > 0 && (
              <span className="bg-red-100 text-red-600 text-xs font-medium px-2 py-0.5 rounded-full">
                {unreadCount} ongelezen
              </span>
            )}
          </div>

          {/* List */}
          <div className="max-h-[60vh] md:max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-slate-400 text-sm">
                Geen meldingen
              </div>
            ) : (
              <ul className="divide-y divide-slate-50">
                {notifications.slice(0, 20).map((n) => (
                  <li
                    key={n.id}
                    className={`px-4 py-3 flex items-start gap-3 ${
                      !n.is_read ? "bg-waves-blue-light/40" : ""
                    }`}
                  >
                    {/* Type icon */}
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-sm ${
                        n.type === "warning"
                          ? "bg-yellow-100 text-yellow-600"
                          : n.type === "success"
                          ? "bg-green-100 text-green-600"
                          : "bg-waves-blue-light text-waves-blue"
                      }`}
                    >
                      {typeIcon[n.type]}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-700 leading-snug">{n.message}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.is_read && (
                      <div className="w-2 h-2 rounded-full bg-waves-blue flex-shrink-0 mt-1.5" />
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Footer */}
          {unreadCount > 0 && (
            <div className="px-4 py-3 border-t border-slate-100">
              <button
                onClick={markAllRead}
                className="flex items-center gap-1.5 text-xs font-medium text-waves-blue hover:text-waves-blue-hover transition-colors"
              >
                <Check size={13} />
                Alles als gelezen markeren
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
